import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { AGREEMENT_STATUS, AGREEMENT_TYPE, PERMISSIONS } from "../../constants";
import {
  AGREEMENT_STATUS_COLOR,
  AGREEMENT_STATUS_LABELS,
  AGREEMENT_TYPE_LABELS,
  formatDate,
  formatMoney,
} from "./agreementLabels";
import AgreementForm from "./AgreementForm";
import AgreementSummary from "./AgreementSummary";
import { daysUntilEnd } from "./agreementLogic";
import {
  fetchAgreementPage,
  fetchAgreementStats,
  fetchExpiryCounts,
} from "../../services/backend/AgreementService";
import {
  DEFAULT_AGREEMENT_CONFIG,
  getAgreementConfig,
} from "../../services/backend/AgreementConfigService";
import {
  appendAgreementList,
  setAgreementFilters,
  setAgreementList,
  setAgreementLoading,
  upsertAgreement,
} from "../../redux/slices/agreementSlice";

const PAGE_SIZE = 25;

// Un fallo de la consulta no puede verse igual que "no hay resultados": cada
// código de error de Firestore se traduce a algo accionable.
const LOAD_ERROR_MESSAGE = {
  "failed-precondition":
    "La consulta necesita un índice de Firestore que aún se está construyendo. Espera unos minutos y vuelve a recargar.",
  "permission-denied":
    "No tienes permiso para consultar estos datos. Revisa las reglas de Firestore del proyecto.",
};

// Color de la barra de comprometido: llegar al tope del convenio es lo que hay
// que ver de un vistazo. El umbral del aviso sale de la configuración; el 100%
// es error pase lo que pase.
const budgetColor = (percentage, warningPercentage) => {
  if (percentage >= 100) return "error";
  if (percentage >= warningPercentage) return "warning";
  return "primary";
};

// Aviso de vigencia de la fila, o null cuando al convenio le queda plazo de
// sobra. Usa la misma ventana que el tablero, que la fija la configuración.
const expiryFlag = (endDate, soonDays) => {
  const days = daysUntilEnd(endDate);
  if (days === null) return null;
  if (days < 0) return { color: "error", label: "Vencido" };
  if (days <= soonDays) return { color: "warning", label: `${days} d` };
  return null;
};

export default function AgreementList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const { list, loading, filters } = useSelector((state) => state.agreement);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  // Cifras que gobierna la configuración del módulo, no el código: cuántos días
  // antes se considera "próximo a vencer" y a partir de qué porcentaje
  // comprometido se avisa. Se arranca con los valores por defecto para que la
  // primera pintada no cambie de umbral a mitad de camino.
  const [soonDays, setSoonDays] = useState(DEFAULT_AGREEMENT_CONFIG.soonDays);
  const [warningPercentage, setWarningPercentage] = useState(
    DEFAULT_AGREEMENT_CONFIG.budgetWarningPercentage
  );

  const perms = user?.permissions || [];
  const roles = user?.roles || [];
  // ADMIN pasa por rol: los permisos son una foto por usuario, así que
  // agreement.* no existe en las cuentas creadas antes de este módulo. Es el
  // mismo criterio que aplica handleAgreement en el servidor.
  const isAdmin = roles.includes("ADMIN");
  const allowed = perms.includes(PERMISSIONS.AGREEMENT.LIST) || isAdmin;
  const canCreate = perms.includes(PERMISSIONS.AGREEMENT.CREATE) || isAdmin;

  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);
  const generationRef = useRef(0);
  const sentinelRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadPage = useCallback(
    async (reset) => {
      if (!reset && inFlightRef.current) return;
      if (reset) {
        generationRef.current += 1;
        cursorRef.current = null;
        setHasMore(true);
        dispatch(setAgreementLoading(true));
      } else {
        setLoadingMore(true);
      }
      const myGen = generationRef.current;
      inFlightRef.current = true;
      try {
        const { items, lastDoc, hasMore: more, error } = await fetchAgreementPage({
          status: filters.status,
          type: filters.type,
          expiry: filters.expiry,
          soonDays,
          cursor: cursorRef.current,
          pageSize: PAGE_SIZE,
        });
        if (myGen !== generationRef.current) return;
        setLoadError(error || null);
        cursorRef.current = lastDoc;
        setHasMore(more);
        if (reset) dispatch(setAgreementList(items));
        else dispatch(appendAgreementList(items));
      } finally {
        if (myGen === generationRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          if (reset) dispatch(setAgreementLoading(false));
        }
      }
    },
    [dispatch, filters.status, filters.type, filters.expiry, soonDays]
  );

  // El tablero describe la cartera COMPLETA, no la porción filtrada: sale del
  // documento de contadores, que es global por construcción. Por eso vive encima
  // de la fila de filtros, que gobierna sólo a la tabla — y por eso al pulsar una
  // tarjeta o un tramo lo que pasa es que se filtra la tabla de abajo.
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    // La ventana de vencimiento sale de la configuración, así que se lee ANTES
    // de contar: preguntar por 30 días y luego rotular "45" sería mentir.
    const cfg = await getAgreementConfig();
    setSoonDays(cfg.soonDays);
    setWarningPercentage(cfg.budgetWarningPercentage);
    const [statsData, expiryData] = await Promise.all([
      fetchAgreementStats(),
      fetchExpiryCounts({ soonDays: cfg.soonDays }),
    ]);
    setStats(statsData);
    setExpiry(expiryData);
    setSummaryLoading(false);
  }, []);

  useEffect(() => {
    if (!allowed) {
      navigate("/home");
      return;
    }
    loadPage(true);
    loadSummary();
  }, [allowed, navigate, loadPage, loadSummary]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || !hasMore || inFlightRef.current) return;
        loadPage(false);
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  // Búsqueda por texto sobre lo ya cargado: los campos que importan (código,
  // objeto, entidad) no son filtrables en Firestore sin un índice de texto, y el
  // universo de convenios es pequeño comparado con el de aspirantes.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((a) =>
      [a.code, a.subject, a.contractingEntity, a.area, a.managerName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [list, search]);

  // Desde el listado sólo se crea; la edición vive en la ficha del convenio.
  const openNew = () => setFormOpen(true);

  const handleSaved = (saved) => {
    if (saved) dispatch(upsertAgreement(saved));
    // El documento guardado vuelve sin los timestamps del servidor, así que se
    // recarga la página para que la lista quede con el orden real. El resumen se
    // recalcula también: un convenio nuevo mueve todas las cifras del tablero.
    loadPage(true);
    loadSummary();
  };

  if (!allowed) return null;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Convenios</Typography>
        <Tooltip title="Recargar">
          <IconButton onClick={() => { loadPage(true); loadSummary(); }}><RefreshIcon /></IconButton>
        </Tooltip>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Nuevo convenio
          </Button>
        )}
      </Stack>

      {/* Tablero: la cartera completa. Va ANTES de los filtros porque no lo
          gobiernan — pulsar una tarjeta o un tramo filtra la tabla de abajo. */}
      <AgreementSummary
        stats={stats}
        expiry={expiry}
        loading={summaryLoading}
        onSelectStatus={(status) => dispatch(setAgreementFilters({ status, expiry: null }))}
        onSelectExpiry={(value) => dispatch(setAgreementFilters({ expiry: value }))}
        warningPercentage={warningPercentage}
      />

      <Typography variant="h6" sx={{ mb: 1.5 }}>Listado de convenios</Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          label="Buscar"
          placeholder="Código, objeto, entidad…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <IconButton size="small" onClick={() => setSearch("")}><ClearIcon fontSize="small" /></IconButton>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={filters.status || ""}
            onChange={(e) => dispatch(setAgreementFilters({ status: e.target.value || null }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(AGREEMENT_STATUS).map((s) => (
              <MenuItem key={s} value={s}>{AGREEMENT_STATUS_LABELS[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={filters.type || ""}
            onChange={(e) => dispatch(setAgreementFilters({ type: e.target.value || null }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(AGREEMENT_TYPE).map((t) => (
              <MenuItem key={t} value={t}>{AGREEMENT_TYPE_LABELS[t]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* El filtro de vigencia se activa desde el tablero, así que tiene que
            poder verse y quitarse desde aquí: si no, la tabla queda acotada sin
            que nada en la fila de filtros lo explique. */}
        {filters.expiry && (
          <Chip
            color={filters.expiry === "expired" ? "error" : "warning"}
            variant="outlined"
            icon={<ReportProblemOutlinedIcon />}
            label={filters.expiry === "expired" ? "Sólo vencidos" : "Sólo próximos a vencer"}
            onDelete={() => dispatch(setAgreementFilters({ expiry: null }))}
          />
        )}
      </Stack>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {LOAD_ERROR_MESSAGE[loadError] || "No se pudieron cargar los convenios."}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Objeto</TableCell>
              <TableCell>Entidad</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Comprometido</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search ? "Ningún convenio coincide con la búsqueda." : "Todavía no hay convenios registrados."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && visible.map((a) => (
              <TableRow
                key={a.code}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/agreements/${encodeURIComponent(a.code)}`)}
              >
                <TableCell>{a.code}</TableCell>
                <TableCell>{AGREEMENT_TYPE_LABELS[a.type] || a.type}</TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" noWrap title={a.subject}>{a.subject}</Typography>
                </TableCell>
                <TableCell>{a.contractingEntity}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>{formatDate(a.startDate)} – {formatDate(a.endDate)}</span>
                    {/* Vencimiento: el aviso viaja con icono y texto, nunca sólo
                        con el color. */}
                    {expiryFlag(a.endDate, soonDays) && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={expiryFlag(a.endDate, soonDays).color}
                        icon={<ReportProblemOutlinedIcon />}
                        label={expiryFlag(a.endDate, soonDays).label}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatMoney(a.totalValue)}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    <LinearProgress
                      variant="determinate"
                      // El ancho de la barra se corta en 100 aunque el dato sea
                      // mayor; el número sigue mostrando la cifra real.
                      value={Math.min(a.committedPercentage || 0, 100)}
                      color={budgetColor(a.committedPercentage || 0, warningPercentage)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {a.committedPercentage || 0}% · saldo {formatMoney(a.remainingValue)}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={AGREEMENT_STATUS_LABELS[a.status] || a.status}
                    color={AGREEMENT_STATUS_COLOR[a.status] || "default"}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        {loadingMore && <CircularProgress size={22} />}
      </Box>

      <AgreementForm
        open={formOpen}
        agreement={null}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </Container>
  );
}
